import type { RelayJournalPersistence } from "@cmmd-forge/contracts";

export interface RelayControlJournalStorage {
  readonly read: () => string | null;
  readonly write: (value: string) => void;
}

export type RelayControlJournalRecordStatus = "pending" | "retrying" | "outcome-unknown" | "failed";

export type DurableJournalPersistence = Exclude<RelayJournalPersistence, "none">;

export interface RelayControlJournalRecord {
  readonly runtimeId: string;
  readonly actorSessionId: string;
  readonly transportRequestId: string;
  readonly durableRequestId: string;
  readonly sequence: number;
  readonly method: string;
  readonly persistence: DurableJournalPersistence;
  readonly frame: string | null;
  readonly status: RelayControlJournalRecordStatus;
  readonly attemptCount: number;
  readonly errorCode: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface RelayControlJournalSnapshot {
  readonly controlSessionId: string;
  readonly records: ReadonlyArray<RelayControlJournalRecord>;
}

export interface RelayControlJournalPrepareInput {
  readonly runtimeId: string;
  readonly actorSessionId: string;
  readonly transportRequestId: string;
  readonly durableRequestId?: string;
  readonly method: string;
  readonly frame: string;
  readonly persistence: DurableJournalPersistence;
}

export type RelayControlRecoveryState =
  | "idle"
  | "pending"
  | "retrying"
  | "outcome-unknown"
  | "failed";

export interface RelayControlRecoverySummary {
  readonly state: RelayControlRecoveryState;
  readonly count: number;
}

export interface RelayControlJournal {
  readonly controlSessionId: string;
  readonly prepare: (input: RelayControlJournalPrepareInput) => RelayControlJournalRecord;
  readonly records: () => ReadonlyArray<RelayControlJournalRecord>;
  readonly pending: () => ReadonlyArray<RelayControlJournalRecord>;
  readonly replayable: (
    runtimeId: string,
    actorSessionId: string,
  ) => ReadonlyArray<RelayControlJournalRecord>;
  readonly fenceActor: (runtimeId: string, actorSessionId: string) => void;
  readonly beginAttempt: (
    runtimeId: string,
    actorSessionId: string,
    transportRequestId: string,
  ) => RelayControlJournalRecord;
  readonly markFailed: (
    runtimeId: string,
    actorSessionId: string,
    transportRequestId: string,
    errorCode: string,
  ) => void;
  readonly complete: (
    runtimeId: string,
    actorSessionId: string,
    transportRequestId: string,
  ) => void;
  readonly acknowledge: (
    runtimeId: string,
    actorSessionId: string,
    transportRequestId: string,
  ) => void;
  readonly subscribe: (listener: (snapshot: RelayControlJournalSnapshot) => void) => () => void;
}

export interface RelayControlJournalOptions {
  readonly storage: RelayControlJournalStorage;
  readonly randomUUID?: () => string;
  readonly now?: () => Date;
  readonly maxRecords?: number;
  readonly maxBytes?: number;
  readonly maxReplayAgeMs?: number;
}

interface RelayControlJournalDocument {
  readonly version: 1;
  readonly controlSessionId: string;
  readonly nextSequence: number;
  readonly records: ReadonlyArray<RelayControlJournalRecord>;
}

const DEFAULT_MAX_RECORDS = 128;
const DEFAULT_MAX_BYTES = 2 * 1024 * 1024;
const DEFAULT_MAX_REPLAY_AGE_MS = 24 * 60 * 60 * 1_000;

export class RelayControlJournalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RelayControlJournalError";
  }
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isIsoDate(value: unknown): value is string {
  return isNonEmptyString(value) && Number.isFinite(Date.parse(value));
}

function isRecordStatus(value: unknown): value is RelayControlJournalRecordStatus {
  return (
    value === "pending" || value === "retrying" || value === "outcome-unknown" || value === "failed"
  );
}

function hasValidRecordIdentity(record: Readonly<Record<string, unknown>>): boolean {
  return (
    isNonEmptyString(record.transportRequestId) &&
    isNonEmptyString(record.runtimeId) &&
    isNonEmptyString(record.actorSessionId) &&
    isNonEmptyString(record.durableRequestId) &&
    isNonEmptyString(record.method)
  );
}

function hasValidRecordSequence(record: Readonly<Record<string, unknown>>): boolean {
  return (
    Number.isSafeInteger(record.sequence) &&
    Number(record.sequence) > 0 &&
    Number.isSafeInteger(record.attemptCount) &&
    Number(record.attemptCount) >= 0
  );
}

function hasValidRecordPersistence(record: Readonly<Record<string, unknown>>): boolean {
  if (record.persistence !== "frame" && record.persistence !== "metadata-only") return false;
  if (record.frame !== null && !isNonEmptyString(record.frame)) return false;
  return record.persistence !== "metadata-only" || record.frame === null;
}

function hasValidRecordOutcome(record: Readonly<Record<string, unknown>>): boolean {
  if (!isRecordStatus(record.status)) return false;
  if (record.errorCode !== null && !isNonEmptyString(record.errorCode)) return false;
  const terminal = record.status === "outcome-unknown" || record.status === "failed";
  if (terminal) return record.frame === null && isNonEmptyString(record.errorCode);
  return record.errorCode === null;
}

function isJournalRecord(value: unknown): value is RelayControlJournalRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const record = value as Readonly<Record<string, unknown>>;
  return (
    hasValidRecordIdentity(record) &&
    hasValidRecordSequence(record) &&
    hasValidRecordPersistence(record) &&
    hasValidRecordOutcome(record) &&
    isIsoDate(record.createdAt) &&
    isIsoDate(record.updatedAt)
  );
}

function decodeJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    throw new RelayControlJournalError("Forge relay control journal is corrupt.");
  }
}

function isJournalDocumentShape(value: unknown): value is Readonly<{
  version: 1;
  controlSessionId: string;
  nextSequence: number;
  records: ReadonlyArray<RelayControlJournalRecord>;
}> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const document = value as Readonly<Record<string, unknown>>;
  return (
    document.version === 1 &&
    isNonEmptyString(document.controlSessionId) &&
    Number.isSafeInteger(document.nextSequence) &&
    Number(document.nextSequence) >= 1 &&
    Array.isArray(document.records) &&
    document.records.every(isJournalRecord)
  );
}

function hasUniqueRecordIdentity(records: ReadonlyArray<RelayControlJournalRecord>): boolean {
  const requestIds = new Set(records.map(recordKey));
  const durableIds = new Set(records.map((record) => record.durableRequestId));
  const sequences = new Set(records.map((record) => record.sequence));
  return (
    requestIds.size === records.length &&
    durableIds.size === records.length &&
    sequences.size === records.length
  );
}

function decodeDocument(value: string): RelayControlJournalDocument {
  const document = decodeJson(value);
  if (!isJournalDocumentShape(document)) {
    throw new RelayControlJournalError("Forge relay control journal is corrupt.");
  }
  const records = document.records;
  const highestSequence = records.reduce(
    (highest, record) => Math.max(highest, record.sequence),
    0,
  );
  if (!hasUniqueRecordIdentity(records) || document.nextSequence <= highestSequence) {
    throw new RelayControlJournalError("Forge relay control journal is corrupt.");
  }
  return {
    version: 1,
    controlSessionId: document.controlSessionId,
    nextSequence: document.nextSequence,
    records,
  };
}

function defaultRandomUUID(): string {
  return crypto.randomUUID();
}

function recordKey(input: {
  readonly runtimeId: string;
  readonly actorSessionId: string;
  readonly transportRequestId: string;
}): string {
  return `${input.runtimeId}\0${input.actorSessionId}\0${input.transportRequestId}`;
}

const RECOVERY_STATE_PRIORITY: Readonly<Record<RelayControlJournalRecordStatus, number>> = {
  pending: 1,
  retrying: 2,
  "outcome-unknown": 3,
  failed: 4,
};

export function summarizeRelayControlRecovery(
  records: ReadonlyArray<RelayControlJournalRecord>,
  runtimeId: string,
): RelayControlRecoverySummary {
  const matching = records.filter((record) => record.runtimeId === runtimeId);
  if (matching.length === 0) return { state: "idle", count: 0 };
  const highest = matching.reduce<RelayControlJournalRecordStatus>(
    (current, record) =>
      RECOVERY_STATE_PRIORITY[record.status] > RECOVERY_STATE_PRIORITY[current]
        ? record.status
        : current,
    "pending",
  );
  return { state: highest, count: matching.filter((record) => record.status === highest).length };
}

export function createRelayControlJournal(
  options: RelayControlJournalOptions,
): RelayControlJournal {
  const randomUUID = options.randomUUID ?? defaultRandomUUID;
  const now = options.now ?? (() => new Date());
  const maxRecords = options.maxRecords ?? DEFAULT_MAX_RECORDS;
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;
  const maxReplayAgeMs = options.maxReplayAgeMs ?? DEFAULT_MAX_REPLAY_AGE_MS;
  if (maxRecords < 1 || maxBytes < 1 || maxReplayAgeMs < 1) {
    throw new RelayControlJournalError("Forge relay control journal limits must be positive.");
  }
  const persisted = options.storage.read();
  const loadedAt = now();
  let document: RelayControlJournalDocument = persisted
    ? decodeDocument(persisted)
    : {
        version: 1,
        controlSessionId: randomUUID(),
        nextSequence: 1,
        records: [],
      };
  const transientFrames = new Map<string, string>();
  const listeners = new Set<(snapshot: RelayControlJournalSnapshot) => void>();

  function snapshot(): RelayControlJournalSnapshot {
    return { controlSessionId: document.controlSessionId, records: [...document.records] };
  }

  function persist(next: RelayControlJournalDocument): void {
    if (next.records.length > maxRecords) {
      throw new RelayControlJournalError("Forge relay control journal reached its record limit.");
    }
    const encoded = JSON.stringify(next);
    if (new TextEncoder().encode(encoded).byteLength > maxBytes) {
      throw new RelayControlJournalError("Forge relay control journal reached its storage limit.");
    }
    options.storage.write(encoded);
    document = next;
    const current = snapshot();
    for (const listener of listeners) listener(current);
  }

  function reconcileRecord(
    record: RelayControlJournalRecord,
    checkedAt: Date,
  ): RelayControlJournalRecord {
    if (record.status !== "pending" && record.status !== "retrying") return record;
    if (record.persistence === "metadata-only" || record.frame === null) {
      return {
        ...record,
        frame: null,
        status: "outcome-unknown",
        errorCode: "RELAY_COMMAND_OUTCOME_UNKNOWN",
        updatedAt: checkedAt.toISOString(),
      };
    }
    if (checkedAt.getTime() - Date.parse(record.createdAt) > maxReplayAgeMs) {
      return {
        ...record,
        frame: null,
        status: "outcome-unknown",
        errorCode: "RELAY_RECOVERY_EXPIRED",
        updatedAt: checkedAt.toISOString(),
      };
    }
    return record;
  }

  const reconciledRecords = document.records.map((record) => reconcileRecord(record, loadedAt));
  const reconciled = reconciledRecords.some((record, index) => record !== document.records[index]);
  if (persisted === null || reconciled) persist({ ...document, records: reconciledRecords });

  function findPreparedRecord(input: RelayControlJournalPrepareInput) {
    const key = recordKey(input);
    return { key, existing: document.records.find((record) => recordKey(record) === key) ?? null };
  }

  function assertPreparedRecordMatches(
    existing: RelayControlJournalRecord,
    input: RelayControlJournalPrepareInput,
    transientFrame: string | undefined,
  ): void {
    const contentMatches = [
      existing.method === input.method,
      existing.persistence === input.persistence,
      transientFrame === undefined || transientFrame === input.frame,
      existing.frame === null || existing.frame === input.frame,
      input.durableRequestId === undefined || existing.durableRequestId === input.durableRequestId,
    ];
    if (contentMatches.includes(false)) {
      throw new RelayControlJournalError(
        `Relay transport request ${input.transportRequestId} was reused with different content.`,
      );
    }
    if (existing.status === "failed" || existing.status === "outcome-unknown") {
      throw new RelayControlJournalError(
        `Relay transport request ${input.transportRequestId} requires an explicit outcome acknowledgement.`,
      );
    }
  }

  function assertDurableRequestIdAvailable(input: RelayControlJournalPrepareInput): void {
    if (
      input.durableRequestId !== undefined &&
      document.records.some((record) => record.durableRequestId === input.durableRequestId)
    ) {
      throw new RelayControlJournalError(
        `Relay durable request ${input.durableRequestId} was reused by another transport request.`,
      );
    }
  }

  function createRecord(input: RelayControlJournalPrepareInput): RelayControlJournalRecord {
    const timestamp = now().toISOString();
    return {
      runtimeId: input.runtimeId,
      actorSessionId: input.actorSessionId,
      transportRequestId: input.transportRequestId,
      durableRequestId: input.durableRequestId ?? randomUUID(),
      sequence: document.nextSequence,
      method: input.method,
      persistence: input.persistence,
      frame: input.persistence === "frame" ? input.frame : null,
      status: "pending",
      attemptCount: 0,
      errorCode: null,
      createdAt: timestamp,
      updatedAt: timestamp,
    };
  }

  function prepare(input: RelayControlJournalPrepareInput): RelayControlJournalRecord {
    const { key, existing } = findPreparedRecord(input);
    if (existing !== null) {
      assertPreparedRecordMatches(existing, input, transientFrames.get(key));
      transientFrames.set(key, input.frame);
      return existing;
    }
    assertDurableRequestIdAvailable(input);
    const record = createRecord(input);
    transientFrames.set(key, input.frame);
    persist({
      ...document,
      nextSequence: document.nextSequence + 1,
      records: [...document.records, record],
    });
    return record;
  }

  function findRecord(
    runtimeId: string,
    actorSessionId: string,
    transportRequestId: string,
  ): RelayControlJournalRecord {
    const existing = document.records.find(
      (record) =>
        record.runtimeId === runtimeId &&
        record.actorSessionId === actorSessionId &&
        record.transportRequestId === transportRequestId,
    );
    if (!existing) {
      throw new RelayControlJournalError(
        `Relay transport request ${transportRequestId} is not journaled.`,
      );
    }
    return existing;
  }

  function markFailed(
    runtimeId: string,
    actorSessionId: string,
    transportRequestId: string,
    errorCode: string,
  ): void {
    findRecord(runtimeId, actorSessionId, transportRequestId);
    const timestamp = now().toISOString();
    transientFrames.delete(recordKey({ runtimeId, actorSessionId, transportRequestId }));
    persist({
      ...document,
      records: document.records.map((record) =>
        record.runtimeId === runtimeId &&
        record.actorSessionId === actorSessionId &&
        record.transportRequestId === transportRequestId
          ? { ...record, frame: null, status: "failed", errorCode, updatedAt: timestamp }
          : record,
      ),
    });
  }

  function beginAttempt(
    runtimeId: string,
    actorSessionId: string,
    transportRequestId: string,
  ): RelayControlJournalRecord {
    const existing = findRecord(runtimeId, actorSessionId, transportRequestId);
    if (existing.status === "failed" || existing.status === "outcome-unknown") {
      throw new RelayControlJournalError(
        `Relay transport request ${transportRequestId} has no replayable outcome.`,
      );
    }
    const updated: RelayControlJournalRecord = {
      ...existing,
      status: existing.attemptCount > 0 ? "retrying" : "pending",
      attemptCount: existing.attemptCount + 1,
      errorCode: null,
      updatedAt: now().toISOString(),
    };
    persist({
      ...document,
      records: document.records.map((record) =>
        record.runtimeId === runtimeId &&
        record.actorSessionId === actorSessionId &&
        record.transportRequestId === transportRequestId
          ? updated
          : record,
      ),
    });
    return updated;
  }

  function remove(runtimeId: string, actorSessionId: string, transportRequestId: string): void {
    const nextRecords = document.records.filter(
      (record) =>
        record.runtimeId !== runtimeId ||
        record.actorSessionId !== actorSessionId ||
        record.transportRequestId !== transportRequestId,
    );
    if (nextRecords.length === document.records.length) return;
    transientFrames.delete(recordKey({ runtimeId, actorSessionId, transportRequestId }));
    persist({ ...document, records: nextRecords });
  }

  function acknowledge(
    runtimeId: string,
    actorSessionId: string,
    transportRequestId: string,
  ): void {
    const existing = findRecord(runtimeId, actorSessionId, transportRequestId);
    if (existing.status !== "failed" && existing.status !== "outcome-unknown") {
      throw new RelayControlJournalError(
        `Relay transport request ${transportRequestId} does not have an acknowledgeable outcome.`,
      );
    }
    remove(runtimeId, actorSessionId, transportRequestId);
  }

  function reconcileReplayability(): void {
    const checkedAt = now();
    const records = document.records.map((record) => reconcileRecord(record, checkedAt));
    if (records.some((record, index) => record !== document.records[index])) {
      persist({ ...document, records });
    }
  }

  function fenceActor(runtimeId: string, actorSessionId: string): void {
    const timestamp = now().toISOString();
    const records = document.records.map((record): RelayControlJournalRecord => {
      if (
        record.runtimeId !== runtimeId ||
        record.actorSessionId === actorSessionId ||
        (record.status !== "pending" && record.status !== "retrying")
      ) {
        return record;
      }
      transientFrames.delete(recordKey(record));
      return {
        ...record,
        frame: null,
        status: "outcome-unknown",
        errorCode: "RELAY_ACTOR_SESSION_CHANGED",
        updatedAt: timestamp,
      };
    });
    if (records.some((record, index) => record !== document.records[index])) {
      persist({ ...document, records });
    }
  }

  return {
    controlSessionId: document.controlSessionId,
    prepare,
    records: () => [...document.records],
    pending: () => [...document.records],
    replayable: (runtimeId, actorSessionId) => {
      reconcileReplayability();
      return document.records.filter(
        (record) =>
          record.runtimeId === runtimeId &&
          record.actorSessionId === actorSessionId &&
          record.persistence === "frame" &&
          record.frame !== null &&
          (record.status === "pending" || record.status === "retrying"),
      );
    },
    fenceActor,
    beginAttempt,
    markFailed,
    complete: remove,
    acknowledge,
    subscribe: (listener) => {
      listeners.add(listener);
      listener(snapshot());
      return () => listeners.delete(listener);
    },
  };
}

export function createMemoryRelayControlJournalStorage(
  initialValue: string | null = null,
): RelayControlJournalStorage {
  let value = initialValue;
  return {
    read: () => value,
    write: (next) => {
      value = next;
    },
  };
}
